/* Just Silver - product gallery layer (08 CONCEPT).
   1. Photos follow the chosen metal. Each slide carries data-jsq-metal, read in
      Liquid from the photo's alt text ("... in rose gold, front view"). Untagged
      photos (styled shots, the gift box) always stay. If nothing matches, every
      photo stays, so a product with untagged photos is never left empty.
   2. Desktop: choosing a metal no longer scrolls the page down to that photo.
   3. Phones: thumbnail strip under the photo when there are more than four.
   4. Phones: a tap opens a fitted lightbox with swipe, pinch and double-tap zoom,
      a counter and a close button, in place of the theme's full-size pan view.
   5. Product ID: tap to copy the code (the button lives in the title block and is
      re-rendered on every variant change, so the listener is delegated). */
(() => {
  if (window.jsqGallery) return;
  window.jsqGallery = true;

  const PHONE = window.matchMedia('(max-width: 47.99em)');
  const config = (() => {
    const el = document.querySelector('script[data-jsq-gallery]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  })();

  const phrase = (value) => String(value || '').toLowerCase().replace(/\s*\+\s*/g, ' and ').replace(/\s+/g, ' ').trim();

  /* ---------- 1. metal filter ------------------------------------------------ */
  const gallery = config ? document.getElementById(`MediaGallery-${config.section}`) : null;
  const slideshow = gallery ? gallery.querySelector('slideshow-component') : null;
  const track = slideshow ? slideshow.querySelector('[id^="Slider-"]') : null;
  const allSlides = track ? [...track.querySelectorAll(':scope > .slideshow__slide')] : [];
  const tagged = allSlides.filter((s) => s.dataset.jsqMetal);
  let current = null;

  const visibleSlides = () => (track ? [...track.querySelectorAll(':scope > .slideshow__slide')] : []);

  const filter = (value, featuredId) => {
    if (!track || tagged.length < 2 || config.colorIndex < 0) return;
    const wanted = phrase(value);
    if (wanted === current) return;
    current = wanted;
    const metalOnly = wanted.includes(' and ') ? wanted.split(' and ')[0] : null;
    /* the chosen finish first, then its metal-only shots (back views), then the
       untagged ones (styled photographs, the gift box), each in the store's order */
    const exact = allSlides.filter((s) => s.dataset.jsqMetal === wanted);
    const metal = metalOnly ? allSlides.filter((s) => s.dataset.jsqMetal === metalOnly) : [];
    const plain = allSlides.filter((s) => !s.dataset.jsqMetal);
    let shown = [...exact, ...metal, ...plain];
    if (!wanted || !exact.length && !metal.length) shown = allSlides;
    track.replaceChildren(...shown);
    shown.forEach((s, i) => {
      s.dataset.index = i;
      s.setAttribute('aria-label', `${i + 1} of ${shown.length}`);
    });
    const firstImg = shown[0] && shown[0].querySelector('img');
    if (firstImg) firstImg.loading = 'eager';
    gallery.dataset.size = shown.length;
    if (slideshow.initPages) {
      slideshow.width = undefined;
      slideshow.currentPage = 1;
      try { slideshow.initPages(); } catch (e) { /* single slide */ }
    }
    let target = shown[0];
    if (featuredId) {
      const match = shown.find((s) => s.dataset.mediaId === String(featuredId));
      if (match) target = match;
    }
    if (slideshow.jumpToSlide && target && PHONE.matches) slideshow.jumpToSlide(target, true);
    buildThumbs();
  };

  const valueOf = (variant) => {
    if (!variant || !config || config.colorIndex < 0) return null;
    return (variant.options || [])[config.colorIndex];
  };

  /* ---------- 2. no page jump on desktop ------------------------------------- */
  const patch = (MediaGallery) => {
    if (!MediaGallery || MediaGallery.prototype.jsqPatched) return;
    MediaGallery.prototype.jsqPatched = true;
    MediaGallery.prototype.changeImage = function (id) {
      if (!id || !this.slideshow) return;
      if (PHONE.matches) {
        const slide = this.slideshow.querySelector(`[data-media-id="${id}"]`);
        if (slide) this.slideshow.jumpToSlide(slide);
      }
    };
  };
  patch(customElements.get('media-gallery'));
  customElements.whenDefined('media-gallery').then(() => patch(customElements.get('media-gallery')));

  document.addEventListener('variant:change', (event) => {
    if (!config || event.detail.sectionId !== config.section) return;
    const variant = event.detail.variant;
    filter(valueOf(variant), variant && variant.featured_media && variant.featured_media.id);
    /* keep the Product ID in the "Ask about this piece" email current */
    const ask = document.querySelector('a[data-jsq-ask]');
    if (ask && variant) {
      const code = String(variant.barcode || '').trim();
      let subject = `Question about ${ask.dataset.jsqAsk}`;
      if (code && code !== '0') subject += `, Product ID ${code}`;
      ask.href = `mailto:customercare@justsilver.co?subject=${encodeURIComponent(subject)}`;
    }
  });

  /* ---------- 3. phone thumbnails -------------------------------------------- */
  /* The strip is reserved in the page by product-media-gallery.liquid when the
     chosen metal has more than four photos, so filling it moves nothing. */
  let thumbs = slideshow && slideshow.parentNode
    ? slideshow.parentNode.querySelector(':scope > [data-jsq-thumbs]')
    : null;
  function buildThumbs() {
    if (!slideshow) return;
    const slides = visibleSlides();
    if (slides.length <= 4) {
      if (thumbs) { thumbs.remove(); thumbs = null; }
      return;
    }
    if (!thumbs) {
      thumbs = document.createElement('div');
      thumbs.className = 'jsq-thumbs';
      thumbs.setAttribute('role', 'group');
      thumbs.setAttribute('aria-label', 'Photos');
      thumbs.dataset.jsqThumbs = '';
    }
    thumbs.replaceChildren();
    slides.forEach((slide, i) => {
      const img = slide.querySelector('img');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'jsq-thumbs__item';
      b.setAttribute('aria-label', `Show photo ${i + 1} of ${slides.length}`);
      if (img) {
        const t = document.createElement('img');
        t.src = sized(img.currentSrc || img.src, 160);
        t.alt = '';
        t.loading = 'lazy';
        t.width = 56;
        t.height = 56;
        b.append(t);
      }
      b.addEventListener('click', () => slideshow.jumpToSlide(slide));
      thumbs.append(b);
    });
    if (!thumbs.isConnected) slideshow.after(thumbs);
    markThumb();
  }
  function markThumb() {
    if (!thumbs || !slideshow) return;
    const i = (slideshow.currentPage || 1) - 1;
    [...thumbs.children].forEach((b, n) => {
      if (n === i) {
        b.setAttribute('aria-current', 'true');
        const left = b.offsetLeft - (thumbs.clientWidth - b.clientWidth) / 2;
        thumbs.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      } else {
        b.removeAttribute('aria-current');
      }
    });
  }
  if (track) {
    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTimeout(markThumb, 60));
    }, { passive: true });
  }

  /* ---------- 4. phone lightbox ---------------------------------------------- */
  function sized(src, width) {
    if (!src) return src;
    try {
      const u = new URL(src, window.location.href);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
  const ICON_PREV = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
  const ICON_NEXT = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';

  const lb = {
    el: null, rail: null, count: null, frames: [], index: 0, opener: null,
    s: 1, x: 0, y: 0, pointers: new Map(), mode: null, start: null, lastTap: null,

    build() {
      const el = document.createElement('div');
      el.className = 'jsq-lb';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Photo viewer');
      el.hidden = true;
      el.innerHTML =
        '<div class="jsq-lb__bar"><p class="jsq-lb__count" aria-live="polite"></p>' +
        `<button type="button" class="jsq-lb__btn jsq-lb__close" aria-label="Close">${ICON_CLOSE}</button></div>` +
        '<div class="jsq-lb__stage"><div class="jsq-lb__rail"></div></div>' +
        `<div class="jsq-lb__foot"><button type="button" class="jsq-lb__btn jsq-lb__prev" aria-label="Previous photo">${ICON_PREV}</button>` +
        '<p class="jsq-lb__hint">Pinch or double-tap to zoom</p>' +
        `<button type="button" class="jsq-lb__btn jsq-lb__next" aria-label="Next photo">${ICON_NEXT}</button></div>`;
      document.body.append(el);
      this.el = el;
      this.rail = el.querySelector('.jsq-lb__rail');
      this.count = el.querySelector('.jsq-lb__count');
      el.querySelector('.jsq-lb__close').addEventListener('click', () => this.close());
      el.querySelector('.jsq-lb__prev').addEventListener('click', () => this.go(this.index - 1));
      el.querySelector('.jsq-lb__next').addEventListener('click', () => this.go(this.index + 1));
      /* on the document, so the keys still work after a tap moves focus off the
         buttons; ignored while the viewer is closed */
      document.addEventListener('keydown', (e) => {
        if (!this.el || this.el.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
        else if (e.key === 'ArrowRight') this.go(this.index + 1);
        else if (e.key === 'ArrowLeft') this.go(this.index - 1);
        else if (e.key === 'Tab') this.trap(e);
      });
      const stage = el.querySelector('.jsq-lb__stage');
      stage.addEventListener('pointerdown', (e) => this.down(e));
      stage.addEventListener('pointermove', (e) => this.move(e));
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((t) => stage.addEventListener(t, (e) => this.up(e)));
    },

    trap(e) {
      const f = [...this.el.querySelectorAll('button:not([disabled])')];
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (!this.el.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },

    open(index, opener) {
      if (!this.el) this.build();
      const slides = visibleSlides();
      this.rail.replaceChildren();
      this.frames = slides.map((slide) => {
        const img = slide.querySelector('img');
        const fig = document.createElement('figure');
        fig.className = 'jsq-lb__frame';
        const big = document.createElement('img');
        big.alt = img ? img.getAttribute('alt') || '' : '';
        big.dataset.src = img ? sized(img.currentSrc || img.src, 1800) : '';
        big.draggable = false;
        fig.append(big);
        this.rail.append(fig);
        return big;
      });
      this.opener = opener || null;
      this.el.hidden = false;
      document.documentElement.classList.add('jsq-lb-open');
      requestAnimationFrame(() => this.el.classList.add('is-open'));
      this.go(index, true);
      this.el.querySelector('.jsq-lb__close').focus({ preventScroll: true });
    },

    close() {
      if (!this.el || this.el.hidden) return;
      this.el.classList.remove('is-open');
      this.el.hidden = true;
      document.documentElement.classList.remove('jsq-lb-open');
      if (slideshow && this.frames.length) {
        const slide = visibleSlides()[this.index];
        if (slide) slideshow.jumpToSlide(slide, true);
      }
      if (this.opener) this.opener.focus({ preventScroll: true });
    },

    go(i, instant) {
      const n = this.frames.length;
      if (!n) return;
      this.index = Math.max(0, Math.min(n - 1, i));
      this.reset(true);
      [this.index - 1, this.index, this.index + 1].forEach((k) => {
        const im = this.frames[k];
        if (im && !im.src && im.dataset.src) im.src = im.dataset.src;
      });
      this.rail.style.transition = instant ? 'none' : '';
      this.rail.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
      this.count.textContent = `${this.index + 1} / ${n}`;
      this.el.querySelector('.jsq-lb__prev').disabled = this.index === 0;
      this.el.querySelector('.jsq-lb__next').disabled = this.index === n - 1;
    },

    img() { return this.frames[this.index]; },

    apply(animate) {
      const im = this.img();
      if (!im) return;
      im.style.transition = animate ? 'transform .3s cubic-bezier(.22,.61,.36,1)' : 'none';
      im.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.s})`;
      this.el.classList.toggle('is-zoomed', this.s > 1.01);
    },

    reset(animate) { this.s = 1; this.x = 0; this.y = 0; this.apply(animate); },

    clamp() {
      const im = this.img();
      if (!im) return;
      const w = im.offsetWidth;
      const h = im.offsetHeight;
      const mx = Math.max(0, (w * this.s - window.innerWidth) / 2);
      const my = Math.max(0, (h * this.s - window.innerHeight) / 2);
      this.x = Math.max(-mx, Math.min(mx, this.x));
      this.y = Math.max(-my, Math.min(my, this.y));
    },

    zoomAt(cx, cy, next) {
      const im = this.img();
      if (!im) return;
      const r = this.el.querySelector('.jsq-lb__stage').getBoundingClientRect();
      const ox = cx - (r.left + r.width / 2);
      const oy = cy - (r.top + r.height / 2);
      const s = Math.max(1, Math.min(4, next));
      this.x = ox - (s / this.s) * (ox - this.x);
      this.y = oy - (s / this.s) * (oy - this.y);
      this.s = s;
      if (s === 1) { this.x = 0; this.y = 0; }
      this.clamp();
    },

    down(e) {
      if (e.target.closest('button')) return;
      e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.mode = 'pinch';
        this.start = { d: Math.hypot(a.x - b.x, a.y - b.y), s: this.s };
        this.rail.style.transition = '';
        this.rail.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
      } else if (this.pointers.size === 1) {
        this.mode = this.s > 1.01 ? 'pan' : 'swipe';
        this.start = { x: e.clientX, y: e.clientY, px: this.x, py: this.y, t: Date.now() };
        if (this.mode === 'swipe') this.rail.style.transition = 'none';
      }
    },

    move(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.mode === 'pinch' && this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        this.zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, this.start.s * (d / this.start.d));
        this.apply(false);
      } else if (this.mode === 'pan') {
        this.x = this.start.px + (e.clientX - this.start.x);
        this.y = this.start.py + (e.clientY - this.start.y);
        this.clamp();
        this.apply(false);
      } else if (this.mode === 'swipe') {
        const dx = e.clientX - this.start.x;
        const w = this.rail.clientWidth || window.innerWidth;
        this.rail.style.transform = `translate3d(${-this.index * w + dx}px, 0, 0)`;
      }
    },

    up(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.delete(e.pointerId);
      if (this.mode === 'swipe') {
        const dx = e.clientX - this.start.x;
        const dy = e.clientY - this.start.y;
        const dt = Date.now() - this.start.t;
        const w = this.rail.clientWidth || window.innerWidth;
        this.rail.style.transition = '';
        if (Math.abs(dx) > w * 0.18 || (Math.abs(dx) > 40 && dt < 250)) {
          this.go(this.index + (dx < 0 ? 1 : -1));
        } else {
          this.rail.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && e.type === 'pointerup') this.tap(e);
        }
      } else if (this.mode === 'pan') {
        const moved = Math.abs(e.clientX - this.start.x) + Math.abs(e.clientY - this.start.y);
        if (moved < 10 && e.type === 'pointerup') this.tap(e);
      }
      if (this.pointers.size === 1 && this.mode === 'pinch') {
        const p = [...this.pointers.values()][0];
        this.mode = this.s > 1.01 ? 'pan' : null;
        this.start = { x: p.x, y: p.y, px: this.x, py: this.y, t: Date.now() };
        if (this.s < 1.05) this.reset(true);
        return;
      }
      if (this.pointers.size === 0) this.mode = null;
    },

    tap(e) {
      const now = Date.now();
      const last = this.lastTap;
      this.lastTap = { t: now, x: e.clientX, y: e.clientY };
      if (last && now - last.t < 320 && Math.abs(e.clientX - last.x) < 30 && Math.abs(e.clientY - last.y) < 30) {
        this.lastTap = null;
        if (this.s > 1.01) this.reset(true);
        else { this.zoomAt(e.clientX, e.clientY, 2.5); this.apply(true); }
      }
    },
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-zoom-button]');
    if (!btn || !PHONE.matches || !gallery || !gallery.contains(btn)) return;
    e.preventDefault();
    e.stopPropagation();
    const slide = btn.closest('.slideshow__slide');
    const index = Math.max(0, visibleSlides().indexOf(slide));
    lb.open(index, btn);
  }, true);

  PHONE.addEventListener('change', () => { if (!PHONE.matches) lb.close(); });

  /* ---------- 5. Product ID copy -------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-jsq-copy]');
    if (!btn) return;
    const code = btn.dataset.jsqCopy;
    const done = () => {
      btn.classList.add('is-copied');
      if (window.utils && window.utils.announce) window.utils.announce(`Product ID ${code} copied`);
      setTimeout(() => btn.classList.remove('is-copied'), 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, () => {});
    }
  });

  /* ---------- start ---------------------------------------------------------- */
  if (config) {
    const start = () => {
      filter(config.selected, config.featured);
      if (!thumbs || !thumbs.childElementCount) buildThumbs();
    };
    if (slideshow && !slideshow.sliderItems && customElements.whenDefined) {
      customElements.whenDefined('slideshow-component').then(() => setTimeout(start, 0));
    } else {
      setTimeout(start, 0);
    }
  }
})();
